import React, { useState, useEffect } from 'react';
import { useUserFeedback } from '../../hooks/useUserFeedback';
import { Button } from '../ui/button';
import { Loader2, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { formatRelativeTime } from '../../utils/time';

interface FeedbackPanelProps {
  sessionId: string | null;
  isVisible: boolean;
}

interface SessionFeedbackFormData {
  welcomeMessageRating?: number;
  welcomeMessageComment?: string;

  wellbeingCheckRating?: number;
  wellbeingCheckComment?: string;

  understandingAdviceRating?: number;
  understandingAdviceComment?: string;

  websiteReferralRating?: number;
  websiteReferralComment?: string;

  adviceRelevanceRating?: number;
  adviceRelevanceComment?: string;

  responseTimingRating?: number;
  responseTimingComment?: string;

  officerReferralRating?: number;
  officerReferralComment?: string;

  conversationStyleRating?: number;
  conversationStyleComment?: string;

  adaptabilityRating?: number;
  adaptabilityComment?: string;

  helpfulnessRating?: number;
  helpfulnessComment?: string;

  overallRating?: number;
  overallComment?: string;

  likelyToUseAgainRating?: number;
  likelyToUseAgainComment?: string;

  testType?: string;

  otherComments?: string;
}

const initialSessionFeedbackData: SessionFeedbackFormData = {
  welcomeMessageRating: 0,
  welcomeMessageComment: '',
  wellbeingCheckRating: 0,
  wellbeingCheckComment: '',
  understandingAdviceRating: 0,
  understandingAdviceComment: '',
  websiteReferralRating: 0,
  websiteReferralComment: '',
  adviceRelevanceRating: 0,
  adviceRelevanceComment: '',
  responseTimingRating: 0,
  responseTimingComment: '',
  officerReferralRating: 0,
  officerReferralComment: '',
  conversationStyleRating: 0,
  conversationStyleComment: '',
  adaptabilityRating: 0,
  adaptabilityComment: '',
  helpfulnessRating: 0,
  helpfulnessComment: '',
  overallRating: 0,
  overallComment: '',
  likelyToUseAgainRating: 0,
  likelyToUseAgainComment: '',
  testType: 'Other',
  otherComments: ''
};

const RatingInput: React.FC<{
  label: string;
  value: number | undefined;
  onChange: (value: number) => void;
  max?: number;
}> = ({ label, value, onChange, max = 10 }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-rm-ink-900">{label}</label>
    <div className="flex space-x-1 flex-wrap items-center">
      {[...Array(max)].map((_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i + 1)}
          className={`w-5 h-5 rounded-full border-2 text-xs font-medium transition-colors ${value === (i + 1)
              ? 'bg-rm-accent-600 border-rm-accent-600 text-white'
              : 'border-rm-neutral-600/30 text-rm-neutral-600 hover:border-rm-accent-500'
            }`}
        >
          {i + 1}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onChange(0)}
        className={`ml-2 px-2 py-1 text-xs font-medium rounded transition-colors ${value === 0
            ? 'bg-rm-neutral-600 text-white'
            : 'border border-rm-neutral-600/30 text-rm-neutral-600 hover:border-rm-neutral-600/50'
          }`}
      >
        N/A
      </button>
    </div>
  </div>
);

// SelectInput component - commented out for future use with test type selection
/* const SelectInput: React.FC<{
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  options: string[];
}> = ({ label, value, onChange, options }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-rm-ink-900">{label}</label>
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full p-2 border border-rm-neutral-600/30 rounded-card text-sm focus:ring-2 focus:ring-rm-accent-500 focus:border-rm-accent-500"
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
);
*/

const SessionFeedbackSection: React.FC<{
  data: SessionFeedbackFormData;
  originalData: SessionFeedbackFormData;
  lastUpdated?: string;
  onChange: (data: SessionFeedbackFormData) => void;
  onSubmit: () => void;
  loading: boolean;
  error: string | null;
  success: boolean;
}> = ({ data, originalData, lastUpdated, onChange, onSubmit, loading, error, success }) => {
  const handleInputChange = (field: keyof SessionFeedbackFormData, value: string | number) => {
    onChange({ ...data, [field]: value });
  };

  // Check if data has changed from original
  const hasChanged = JSON.stringify(data) !== JSON.stringify(originalData);

  // Validation: At least one question should be answered (not 0, which means N/A)
  const hasAnyRating = Object.keys(data).some(key =>
    key.endsWith('Rating') && data[key as keyof SessionFeedbackFormData] !== 0
  );

  const feedbackQuestions = [
    {
      key: 'welcomeMessage',
      label: '1. How good do you experience the welcome message of Kim?',
      ratingField: 'welcomeMessageRating' as const,
      commentField: 'welcomeMessageComment' as const
    },
    {
      key: 'wellbeingCheck',
      label: '2. How do you experience the wellbeing check-in?',
      ratingField: 'wellbeingCheckRating' as const,
      commentField: 'wellbeingCheckComment' as const
    },
    {
      key: 'understandingAdvice',
      label: '3. How do you experience Kim´s ability to understand your issue and give correct advice?',
      ratingField: 'understandingAdviceRating' as const,
      commentField: 'understandingAdviceComment' as const
    },
    {
      key: 'websiteReferral',
      label: '4. How do you experience Kim´s ability to refer to the Renewmyday content (useful resources like video, audio, articles and recipies)?',
      ratingField: 'websiteReferralRating' as const,
      commentField: 'websiteReferralComment' as const
    },
    {
      key: 'adviceRelevance',
      label: '5. How relevant do you experience the advice Kim gives in relation to your problem?',
      ratingField: 'adviceRelevanceRating' as const,
      commentField: 'adviceRelevanceComment' as const
    },
    {
      key: 'responseTiming',
      label: '6. How do you experience the timing of Kim´s replies (How fast you get a reply)?',
      ratingField: 'responseTimingRating' as const,
      commentField: 'responseTimingComment' as const
    },
    {
      key: 'officerReferral',
      label: '7. How do you experience Kim´s referral to a human Wellbeing Officer (timing of the referral, instructions of referral)?',
      ratingField: 'officerReferralRating' as const,
      commentField: 'officerReferralComment' as const
    },
    {
      key: 'conversationStyle',
      label: '8. How do you experience Kim´s way of speaking (Does it feel like a natural conversation with a human being)?',
      ratingField: 'conversationStyleRating' as const,
      commentField: 'conversationStyleComment' as const
    },
    {
      key: 'adaptability',
      label: '9. How well does Kim adapt her tone, complexity and flow in accordance with your questions?',
      ratingField: 'adaptabilityRating' as const,
      commentField: 'adaptabilityComment' as const
    },
    {
      key: 'helpfulness',
      label: '10. How well does Kim offer help without overwhelming, judging, or ignoring signals?',
      ratingField: 'helpfulnessRating' as const,
      commentField: 'helpfulnessComment' as const
    },
    {
      key: 'overall',
      label: '11. How would you rate your overall experience with Kim?',
      ratingField: 'overallRating' as const,
      commentField: 'overallComment' as const
    },
    {
      key: 'likelyToUseAgain',
      label: '12. How likely is it you address yourself to Kim next time you face a challenge?',
      ratingField: 'likelyToUseAgainRating' as const,
      commentField: 'likelyToUseAgainComment' as const
    }
  ];

  return (
    <div className="space-y-6 p-4">
      {lastUpdated && (
        <div className="text-xs text-rm-neutral-600 bg-rm-bg-tint rounded-card p-3 border border-rm-accent-300/30 mb-4">
          You can update your feedback to reflect how your experience changed over time, we will save each version.
        </div>
      )}
      <div className="text-sm text-rm-neutral-600 mb-4">
        1 - very bad 10 - very good, N/A - not applicable
      </div>
      {feedbackQuestions.map((question) => (
        <div key={question.key} >
          <RatingInput
            label={question.label}
            value={data[question.ratingField]}
            onChange={(value) => handleInputChange(question.ratingField, value)}
            max={10}
          />

          {data[question.ratingField] !== 0 && data[question.ratingField] !== undefined && (
            <div className="space-y-1 mt-3">
              <label className="block text-sm font-medium text-rm-neutral-600">
                Comment
              </label>
              <textarea
                value={data[question.commentField] || ''}
                onChange={(e) => handleInputChange(question.commentField, e.target.value)}
                className="w-full p-2 border border-rm-neutral-600/30 rounded-card text-sm resize-none focus:ring-2 focus:ring-rm-accent-500 focus:border-rm-accent-500"
                rows={2}
                placeholder="Additional comments for this question..."
              />
            </div>
          )}
        </div>
      ))}

      {/* Test type selection - commented out for future use, default "Other" is sent to backend */}
      {/* <div>
        <SelectInput
          label="Test type"
          value={data.testType}
          onChange={(value) => handleInputChange('testType', value)}
          options={[
            'Problem Aware/Mindful Achiever',
            'Problem Aware/Sceptical Beginner',
            'Problem Aware/Crises State',
            'Problem Unaware/Mindful Achiever',
            'Problem Unaware/Sceptical Beginner',
            'Problem Unaware/Crises State',
            'Other'
          ]}
        />
      </div> */}

      <div >
        <div className="space-y-2">
          <label className="block text-sm font-medium text-rm-ink-900">
            Other Comments
          </label>
          <textarea
            value={data.otherComments || ''}
            onChange={(e) => handleInputChange('otherComments', e.target.value)}
            className="w-full p-2 border border-rm-neutral-600/30 rounded-card text-sm resize-none focus:ring-2 focus:ring-rm-accent-500 focus:border-rm-accent-500"
            rows={3}
            placeholder="Any other feedback or suggestions..."
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-center space-x-2 text-green-600 text-sm">
          <CheckCircle className="w-4 h-4" />
          <span>Session feedback saved successfully!</span>
        </div>
      )}

      {lastUpdated && (
        <div className="text-xs text-rm-neutral-600 mb-4">
          Last updated: {formatRelativeTime(lastUpdated)}
        </div>
      )}

      <Button
        onClick={onSubmit}
        disabled={loading || !hasAnyRating || !hasChanged}
        className="w-full"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-4 h-4 mr-2" />
            Save Session Feedback
          </>
        )}
      </Button>
    </div>
  );
};

export const FeedbackPanel: React.FC<FeedbackPanelProps> = ({ sessionId, isVisible }) => {
  const {
    sessionFeedback,
    sessionFeedbackLoading,
    sessionFeedbackError,
    loadSessionFeedback,
    submitSessionFeedback,
    clearSessionFeedback,
    clearErrors
  } = useUserFeedback();

  // Local form state for session feedback only
  const [sessionData, setSessionData] = useState<SessionFeedbackFormData>(initialSessionFeedbackData);
  const [originalSessionData, setOriginalSessionData] = useState<SessionFeedbackFormData>(initialSessionFeedbackData);
  const [sessionSuccess, setSessionSuccess] = useState(false);

  // Load existing session feedback when component mounts or sessionId changes
  useEffect(() => {
    if (isVisible) {
      // Reset session form data when sessionId changes
      setSessionData(initialSessionFeedbackData);
      setOriginalSessionData(initialSessionFeedbackData);
      setSessionSuccess(false);

      if (sessionId) {
        // Clear previous session feedback first, then load new one
        clearSessionFeedback();
        loadSessionFeedback(sessionId);
      }
      clearErrors();
    }
  }, [isVisible, sessionId, loadSessionFeedback, clearSessionFeedback, clearErrors]);

  // Reset session form data when sessionId changes (additional cleanup)
  useEffect(() => {
    if (sessionId) {
      setSessionData(initialSessionFeedbackData);
      setOriginalSessionData(initialSessionFeedbackData);
      setSessionSuccess(false);
    }
  }, [sessionId]);

  // Update form data when session feedback loads
  useEffect(() => {
    if (sessionFeedback?.feedbackData) {
      const loadedData: SessionFeedbackFormData = {
        welcomeMessageRating: sessionFeedback.feedbackData.welcomeMessageRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.welcomeMessageRating || 0,
        welcomeMessageComment: sessionFeedback.feedbackData.welcomeMessageComment || '',
        wellbeingCheckRating: sessionFeedback.feedbackData.wellbeingCheckRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.wellbeingCheckRating || 0,
        wellbeingCheckComment: sessionFeedback.feedbackData.wellbeingCheckComment || '',
        understandingAdviceRating: sessionFeedback.feedbackData.understandingAdviceRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.understandingAdviceRating || 0,
        understandingAdviceComment: sessionFeedback.feedbackData.understandingAdviceComment || '',
        websiteReferralRating: sessionFeedback.feedbackData.websiteReferralRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.websiteReferralRating || 0,
        websiteReferralComment: sessionFeedback.feedbackData.websiteReferralComment || '',
        adviceRelevanceRating: sessionFeedback.feedbackData.adviceRelevanceRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.adviceRelevanceRating || 0,
        adviceRelevanceComment: sessionFeedback.feedbackData.adviceRelevanceComment || '',
        responseTimingRating: sessionFeedback.feedbackData.responseTimingRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.responseTimingRating || 0,
        responseTimingComment: sessionFeedback.feedbackData.responseTimingComment || '',
        officerReferralRating: sessionFeedback.feedbackData.officerReferralRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.officerReferralRating || 0,
        officerReferralComment: sessionFeedback.feedbackData.officerReferralComment || '',
        conversationStyleRating: sessionFeedback.feedbackData.conversationStyleRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.conversationStyleRating || 0,
        conversationStyleComment: sessionFeedback.feedbackData.conversationStyleComment || '',
        adaptabilityRating: sessionFeedback.feedbackData.adaptabilityRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.adaptabilityRating || 0,
        adaptabilityComment: sessionFeedback.feedbackData.adaptabilityComment || '',
        helpfulnessRating: sessionFeedback.feedbackData.helpfulnessRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.helpfulnessRating || 0,
        helpfulnessComment: sessionFeedback.feedbackData.helpfulnessComment || '',
        overallRating: sessionFeedback.feedbackData.overallRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.overallRating || 0,
        overallComment: sessionFeedback.feedbackData.overallComment || '',
        likelyToUseAgainRating: sessionFeedback.feedbackData.likelyToUseAgainRating === 'not-applicable' ? 0 : sessionFeedback.feedbackData.likelyToUseAgainRating || 0,
        likelyToUseAgainComment: sessionFeedback.feedbackData.likelyToUseAgainComment || '',
        testType: sessionFeedback.feedbackData.testType || 'Other',
        otherComments: sessionFeedback.feedbackData.otherComments || ''
      };
      setSessionData(loadedData);
      setOriginalSessionData(loadedData);
    } else {
      // Reset to initial values if no feedback is found
      setSessionData(initialSessionFeedbackData);
      setOriginalSessionData(initialSessionFeedbackData);
    }
  }, [sessionFeedback]);

  // Handle session form submission
  const handleSessionSubmit = async () => {
    if (!sessionId) return;

    setSessionSuccess(false);
    try {
      const success = await submitSessionFeedback(sessionId, sessionData);
      if (success) {
        // Reload data from backend to ensure UI is in sync
        await loadSessionFeedback(sessionId);
        setSessionSuccess(true);
        setTimeout(() => setSessionSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Session feedback submission error:', error);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-rm-neutral-600/20 flex-shrink-0 bg-rm-bg-50">
        <h3 className="text-lg font-semibold text-rm-primary-700">Session Feedback for Kim</h3>
        {/* <p className="text-sm text-gray-600 mt-1">Rate your experience with Kim's assistance in this session</p> */}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Session Feedback Section - only show if there's an active session */}
        {sessionId && (
          <SessionFeedbackSection
            data={sessionData}
            originalData={originalSessionData}
            lastUpdated={sessionFeedback?.updatedAt || sessionFeedback?.createdAt}
            onChange={setSessionData}
            onSubmit={handleSessionSubmit}
            loading={sessionFeedbackLoading}
            error={sessionFeedbackError}
            success={sessionSuccess}
          />
        )}

        {!sessionId && (
          <div className="p-4 border border-rm-neutral-600/20 rounded-card text-center text-rm-neutral-600">
            <p>No active session. Session feedback will be available when you start a chat.</p>
          </div>
        )}
      </div>
    </div>
  );
};
